import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';

const Home = lazy(() => import('../pages/home/page'));
const Forums = lazy(() => import('../pages/forums/page'));
const ForumPostDetail = lazy(() => import('../pages/forums/post/page'));
const Polls = lazy(() => import('../pages/polls/page'));
const PollDetail = lazy(() => import('../pages/polls/detail/page'));
const Documents = lazy(() => import('../pages/documents/page'));
const Notifications = lazy(() => import('../pages/notifications/page'));
const Moderation = lazy(() => import('../pages/moderation/page'));
const MemberVerification = lazy(() => import('../pages/member-verification/page'));
const Profile = lazy(() => import('../pages/profile/page'));
const UserProfile = lazy(() => import('../pages/user-profile/page'));
const Terms = lazy(() => import('../pages/terms/page'));
const Login = lazy(() => import('../pages/login/page'));
const Register = lazy(() => import('../pages/register/page'));
const PendingVerification = lazy(() => import('../pages/pending-verification/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Events = lazy(() => import('../pages/events/page'));
const EventDetail = lazy(() => import('../pages/events/detail/page'));
const ActivityLogs = lazy(() => import('../pages/activity-logs/page'));
const ForgotPassword = lazy(() => import('../pages/forgot-password/page'));
const ResetPassword = lazy(() => import('../pages/reset-password/page'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Home />
      </Suspense>
    )
  },
  {
    path: '/forums',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <Forums />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/forums/:postId',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <ForumPostDetail />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/polls',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <Polls />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/polls/:pollId',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <PollDetail />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/documents',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <Documents />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/notifications',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/moderation',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireModerator={true}>
          <Moderation />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/member-verification',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireModerator={true}>
          <MemberVerification />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/profile',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/user/:userId',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <UserProfile />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/terms',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Terms />
      </Suspense>
    )
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Login />
      </Suspense>
    )
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Register />
      </Suspense>
    )
  },
  {
    path: '/pending-verification',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <PendingVerification />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/activity-logs',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireModerator={true}>
          <ActivityLogs />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/events',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <Events />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/events/:id',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute requireVerified={true}>
          <EventDetail />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFound />
      </Suspense>
    )
  }
];

export default routes;
